import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getDefaultCompany, type Company } from "../../../../../api/companies";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useOrderingMessage } from "../../useOrderingMessage";

// Ключът на `useDefaultCompany` — модалът за фактура чете същия кеш
const DEFAULT_COMPANY_KEY = ["companies", "default"];

/**
 * „Фактуриране" от „Опции": текущата фирма се тегли ЧАК при клика (не при
 * всяко връщане към масите; модалът после си я тегли свежа за номера на
 * фактурата). Без текуща фирма (404) потокът не се отваря — съобщението го
 * казва по-бързо от модал. Друга грешка (без връзка, 5xx) НЕ значи „няма
 * фирма": идвала ли е фирмата в тази сесия — модалът се отваря както преди
 * (той си я тегли пак); иначе — причината на грешката. `onOpen` получава
 * фирмата (свежата, иначе от кеша) — по режима ѝ се решава входът.
 */
export const useOpenInvoicing = (onOpen: (company: Company | undefined) => void) => {
  const queryClient = useQueryClient();
  const message = useOrderingMessage();
  const { t } = useTranslation();

  return async () => {
    try {
      const company = await queryClient.fetchQuery({
        queryKey: DEFAULT_COMPANY_KEY,
        queryFn: getDefaultCompany,
        staleTime: 0,
        retry: false,
      });
      onOpen(company);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) message.error(t("invoicing.needDefaultCompany"));
      else if (queryClient.getQueryData(DEFAULT_COMPANY_KEY))
        onOpen(queryClient.getQueryData<Company>(DEFAULT_COMPANY_KEY));
      else message.error(getApiErrorMessage(error, t("common.error")));
    }
  };
};
